// [2026-09-27] ADDED — the InCruiter sidebar every other product now renders, ported here.
//
// SOURCE. This is `InCruiterSidebar` and `SidebarSubItem` from the shared InCruiter component
// library `@contact_incruiter/fcc` 1.0.9 (src/shared/InCruiterSidebar.tsx, read from the package's
// own source map), which frontend-account and frontend-incbot both mount as their sidebar. The
// markup, every inline style and every token below are that file's, translated from TSX to JSX and
// otherwise unchanged — so this rail is the same furniture a customer sees in IncBot and the account
// portal, not a lookalike drawn from a screenshot.
//
// WHY A PORT AND NOT `npm install @contact_incruiter/fcc`. The library ships its other components as
// Tailwind utility classes with NO compiled stylesheet (the consumer builds the CSS), and depends on
// 47 packages — the whole Radix UI kit, fonts, form libraries. Installing it would put all of that,
// plus a Tailwind build and its global reset, into this app AND proctoring-frontend-admin (which
// compiles against this package), to render one component that happens to use only inline styles.
// The component itself needs nothing but React; its icons are in ./lucide.jsx.
//
// KEEPING IT IN STEP. If the library's sidebar changes, diff src/shared/InCruiterSidebar.tsx in the
// new version (`npm pack @contact_incruiter/fcc@<v>`, read dist/index.js.map → sourcesContent) and
// carry the change over here. The two deliberate differences are marked DIFFERENCE below.

import { useEffect, useState } from 'react'
import { ChevronDown, ChevronsUpDown, CreditCard, Menu, UserCircle } from './lucide.jsx'

/* ── Design tokens (verbatim) ─────────────────────────────────────── */
const BRAND = '#12b6bc'
const ACTIVE_BG = '#f5fbf8'
const HOVER_BG = 'rgba(0,0,0,0.03)'
const BORDER = 'rgba(0,0,0,0.07)'

/* ── Default profile items (verbatim) ─────────────────────────────── */
const DEFAULT_PROFILE_ITEMS = [
  { label: 'My Profile', Icon: UserCircle },
  { label: 'Accounts & Billing', Icon: CreditCard },
]

/* ── NavRow (verbatim) ────────────────────────────────────────────── */
function NavRow({ item, active, collapsed, expanded, onNavigate, onToggle }) {
  const { label, href, link, Icon, badge, hasChevron, expandedContent, collapsedExpandedContent, onClick } = item

  const handleClick = () => {
    if (onClick) {
      onClick()
    } else if (link) {
      window.open(link, '_blank', 'noopener,noreferrer')
    } else if (hasChevron && expandedContent && href) {
      onToggle?.(href)
    } else if (href) {
      onNavigate?.(href)
    }
  }

  return (
    <>
      <button
        onClick={handleClick}
        title={collapsed ? label : undefined}
        style={{
          display: 'flex',
          width: '100%',
          alignItems: 'center',
          overflow: 'hidden',
          gap: collapsed ? 0 : 20,
          paddingLeft: collapsed ? 0 : 15,
          paddingRight: collapsed ? 0 : 10,
          paddingTop: 10,
          paddingBottom: 10,
          justifyContent: collapsed ? 'center' : undefined,
          background: active || expanded ? ACTIVE_BG : 'transparent',
          borderLeft: active || expanded ? `2px solid ${BRAND}` : '2px solid transparent',
          borderTop: 'none',
          borderRight: 'none',
          borderBottom: 'none',
          borderRadius: 0,
          cursor: 'pointer',
          outline: 'none',
          transition: 'background 0.12s',
        }}
        onMouseEnter={(e) => {
          if (!active && !expanded) e.currentTarget.style.background = HOVER_BG
        }}
        onMouseLeave={(e) => {
          if (!active && !expanded) e.currentTarget.style.background = 'transparent'
        }}
      >
        <Icon
          size={22}
          style={{ flexShrink: 0, color: active || expanded ? BRAND : '#9ca3af', display: 'block' }}
        />
        {!collapsed && (
          <>
            <span
              style={{
                flex: 1,
                textAlign: 'left',
                fontFamily: "'Manrope', sans-serif",
                fontSize: 13,
                lineHeight: '24px',
                fontWeight: active || expanded ? 700 : 500,
                color: active || expanded ? BRAND : '#000',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {label}
            </span>
            {badge !== undefined && (
              <span
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: 19,
                  minWidth: 20,
                  paddingInline: 4,
                  borderRadius: 10,
                  background: 'rgba(18,182,188,0.14)',
                  color: BRAND,
                  fontSize: 10,
                  fontWeight: 600,
                  flexShrink: 0,
                }}
              >
                {badge}
              </span>
            )}
            {hasChevron && expandedContent && (
              <ChevronDown
                size={16}
                style={{
                  flexShrink: 0,
                  color: '#9ca3af',
                  transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s',
                }}
              />
            )}
            {hasChevron && !expandedContent && (
              <svg
                width={16}
                height={16}
                viewBox="0 0 24 24"
                fill="none"
                stroke="#9ca3af"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ flexShrink: 0, transform: 'rotate(-90deg)' }}
                aria-hidden="true"
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
            )}
          </>
        )}
      </button>

      {!collapsed && expanded && expandedContent && (
        <div
          style={{
            background: 'rgba(18,182,188,0.02)',
            borderLeft: `2px solid ${BRAND}`,
            overflow: 'hidden',
            maxHeight: 320,
            overflowY: 'auto',
          }}
        >
          {expandedContent}
        </div>
      )}

      {collapsed && expanded && collapsedExpandedContent && (
        <div
          style={{
            overflow: 'hidden',
            maxHeight: 320,
            overflowY: 'auto',
          }}
        >
          {collapsedExpandedContent}
        </div>
      )}
    </>
  )
}

/* ── SidebarSubItem (verbatim) ────────────────────────────────────── */
export function SidebarSubItem({ name, icon, active, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        width: '100%',
        padding: '9px 16px 9px 20px',
        textAlign: 'left',
        fontSize: 13,
        fontWeight: active ? 700 : 400,
        color: active ? BRAND : '#374151',
        background: active ? 'rgba(18,182,188,0.08)' : 'transparent',
        border: 'none',
        cursor: 'pointer',
        outline: 'none',
        transition: 'background 0.12s, color 0.12s',
      }}
      onMouseEnter={(e) => {
        if (!active) {
          e.currentTarget.style.background = 'rgba(0,0,0,0.03)'
          e.currentTarget.style.color = '#111'
        }
      }}
      onMouseLeave={(e) => {
        if (!active) {
          e.currentTarget.style.background = 'transparent'
          e.currentTarget.style.color = '#374151'
        }
      }}
    >
      {icon ? (
        <img
          src={icon}
          alt={name}
          style={{ width: 20, height: 20, borderRadius: 4, objectFit: 'cover', flexShrink: 0 }}
        />
      ) : (
        <div
          style={{
            width: 20,
            height: 20,
            borderRadius: 4,
            flexShrink: 0,
            background: active ? 'rgba(18,182,188,0.15)' : 'rgba(0,0,0,0.06)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 10,
            fontWeight: 700,
            color: active ? BRAND : '#9CA3AF',
          }}
        >
          {name.charAt(0)}
        </div>
      )}
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {name}
      </span>
    </button>
  )
}

/* ── InCruiterSidebar ─────────────────────────────────────────────── */
// DIFFERENCE 1 — renders ONLY the <aside>. The library wraps it in
//   <div style={{ display: 'flex', height: '100%' }}><aside/><div>{children}</div></div>
// so the page scrolls inside that column. This dashboard scrolls the WINDOW (fixed header, fixed
// rail, long content column), and switching that would change scrolling on every screen. So the
// host (PlatformShell) puts this <aside> in a fixed, full-height wrapper instead: same box, same
// place, and nothing about how the pages scroll changes.
//
// DIFFERENCE 2 — `profileTitle`, an optional tooltip on the profile row. This dashboard's old
// account menu was the one place that showed the signed-in email and company code, which is what
// someone checks when the numbers look like the wrong account; the tooltip keeps them one hover away.
// Absent → no title attribute, exactly as the library renders.
export function InCruiterSidebar({
  logoSrc,
  user = {},
  navItems = [],
  otherItems = [],
  profileItems,
  activeHref = '/',
  onNavigate,
  collapsed: collapsedProp,
  onToggleCollapse,
  defaultExpandedItem,
  profileTitle,
}) {
  const profileNav = profileItems ?? DEFAULT_PROFILE_ITEMS

  const isProfileActive = profileNav.some(
    (item) =>
      item.href &&
      activeHref &&
      (activeHref === item.href || activeHref.startsWith(item.href + '/')),
  )

  const [collapsedInternal, setCollapsedInternal] = useState(false)
  const collapsed = collapsedProp !== undefined ? collapsedProp : collapsedInternal
  const setCollapsed = onToggleCollapse
    ? (fn) => {
        const next = typeof fn === 'function' ? fn(collapsed) : fn
        onToggleCollapse(next)
      }
    : (fn) => setCollapsedInternal(typeof fn === 'function' ? fn(collapsedInternal) : fn)

  const [profileOpen, setProfileOpen] = useState(isProfileActive)
  const [expandedItem, setExpandedItem] = useState(defaultExpandedItem ?? null)

  useEffect(() => {
    if (isProfileActive) setProfileOpen(true)
  }, [isProfileActive])

  // Re-open the default item whenever the sidebar becomes uncollapsed or the default changes
  useEffect(() => {
    if (!collapsed) {
      setExpandedItem(defaultExpandedItem ?? null)
    }
  }, [collapsed, defaultExpandedItem])

  const initials = user.name
    ? user.name
        .split(' ')
        .map((n) => n[0])
        .join('')
        .slice(0, 2)
        .toUpperCase()
    : '?'

  const handleToggle = (href) => {
    setExpandedItem((prev) => (prev === href ? null : href))
  }

  return (
    <aside
      style={{
        width: collapsed ? 56 : 250,
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        background: '#fff',
        borderRight: `1px solid ${BORDER}`,
        transition: 'width 0.2s ease',
        overflow: 'hidden',
        zIndex: 100,
      }}
    >
      {/* Logo row */}
      <div
        style={{
          height: 52,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'space-between',
          padding: 10,
          borderBottom: `1px solid ${BORDER}`,
        }}
      >
        {!collapsed && logoSrc && (
          <img
            src={logoSrc}
            alt="Logo"
            style={{ height: 36, width: 'auto', objectFit: 'contain' }}
          />
        )}
        <button
          onClick={() => setCollapsed((v) => !v)}
          style={{
            width: 28,
            height: 28,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 6,
            border: 'none',
            background: 'transparent',
            color: '#9ca3af',
            cursor: 'pointer',
            flexShrink: 0,
            outline: 'none',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = '#f3f4f6' }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent' }}
          aria-label="Toggle sidebar"
        >
          <Menu size={16} />
        </button>
      </div>

      {/* User profile */}
      <div style={{ flexShrink: 0 }}>
        <button
          onClick={() => {
            if (collapsed) {
              setCollapsed(false)
              setProfileOpen(true)
            } else {
              setProfileOpen((v) => !v)
            }
          }}
          title={profileTitle || undefined}
          style={{
            display: 'flex',
            width: '100%',
            alignItems: 'center',
            gap: 12,
            padding: 12,
            justifyContent: collapsed ? 'center' : undefined,
            background: isProfileActive ? '#f5fbf8' : 'rgba(18,182,188,0.05)',
            borderLeft: isProfileActive ? '2px solid #12b6bc' : '2px solid transparent',
            borderTop: 'none',
            borderRight: 'none',
            borderBottom: 'none',
            cursor: 'pointer',
            outline: 'none',
          }}
        >
          {user.avatarUrl ? (
            <img
              src={user.avatarUrl}
              alt={user.name ?? 'User'}
              style={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                objectFit: 'cover',
                flexShrink: 0,
                outline: isProfileActive ? '2px solid #12b6bc' : 'none',
                outlineOffset: 2,
              }}
            />
          ) : (
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 11,
                fontWeight: 700,
                outline: isProfileActive ? '2px solid #12b6bc' : 'none',
                outlineOffset: 2,
                color: '#fff',
                background: 'linear-gradient(135deg, #3b82f6 0%, #12b6bc 100%)',
              }}
            >
              {initials}
            </div>
          )}
          {!collapsed && (
            <>
              <div style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                <p
                  style={{
                    margin: 0,
                    fontFamily: "'Manrope', sans-serif",
                    fontSize: 14,
                    fontWeight: 500,
                    color: '#000',
                    lineHeight: 1.45,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {user.name ?? 'User'}
                </p>
                <p
                  style={{
                    margin: 0,
                    fontFamily: "'Manrope', sans-serif",
                    fontSize: 12,
                    fontWeight: 400,
                    color: '#3f3f3f',
                    lineHeight: 1.5,
                    marginTop: 1,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {user.role ?? ''}
                </p>
              </div>
              <ChevronsUpDown size={16} style={{ color: '#9ca3af', flexShrink: 0 }} />
            </>
          )}
        </button>

        {(profileOpen || (collapsed && isProfileActive)) && (
          <>
            {profileNav.map((item) => (
              <NavRow
                key={item.label}
                item={{
                  ...item,
                  onClick: item.onClick
                    ? () => {
                        if (!collapsed) setProfileOpen(false)
                        item.onClick?.()
                      }
                    : item.link
                      ? () => {
                          if (!collapsed) setProfileOpen(false)
                          window.open(item.link, '_blank', 'noopener,noreferrer')
                        }
                      : undefined,
                }}
                active={
                  !!(
                    item.href &&
                    activeHref &&
                    (activeHref === item.href || activeHref.startsWith(item.href + '/'))
                  )
                }
                collapsed={collapsed}
                onNavigate={onNavigate}
                onToggle={handleToggle}
              />
            ))}
          </>
        )}
      </div>

      {/* Main nav */}
      <nav style={{ flex: 1, overflowY: 'auto' }}>
        {navItems.length > 0 && !collapsed && (
          <div
            style={{
              paddingLeft: 20,
              paddingRight: 10,
              paddingTop: 10,
              paddingBottom: 6,
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: '0.06em',
              color: 'rgba(0,0,0,0.45)',
              fontFamily: "'Manrope', sans-serif",
            }}
          >
            MAIN MENU
          </div>
        )}
        {navItems.map((item) => (
          <NavRow
            key={item.href ?? item.label}
            item={item}
            active={activeHref === item.href}
            expanded={expandedItem === item.href}
            collapsed={collapsed}
            onNavigate={onNavigate}
            onToggle={handleToggle}
          />
        ))}
      </nav>

      {/* Other nav */}
      {otherItems.length > 0 && (
        <div style={{ flexShrink: 0 }}>
          {!collapsed && (
            <div
              style={{
                paddingLeft: 20,
                paddingRight: 10,
                paddingTop: 8,
                paddingBottom: 6,
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: '0.06em',
                color: 'rgba(0,0,0,0.45)',
                fontFamily: "'Manrope', sans-serif",
                borderTop: `1px solid ${BORDER}`,
              }}
            >
              OTHER
            </div>
          )}
          {collapsed && <div style={{ height: 8, borderTop: `1px solid ${BORDER}` }} />}
          {otherItems.map((item) => (
            <NavRow
              key={item.href ?? item.label}
              item={item}
              active={activeHref === item.href}
              collapsed={collapsed}
              onNavigate={onNavigate}
              onToggle={handleToggle}
            />
          ))}
          <div style={{ height: 16 }} />
        </div>
      )}
    </aside>
  )
}
